/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isNumber from 'lodash.isnumber';
import isUndefined from 'lodash.isundefined';
import {translate} from '../locale/locale';
import {Results} from '../services/GroupProperties';
import {
  AssetBulkEditorFormFields
} from '../types/editors';
import {
  AccountLink,
  Asset,
  CTCEntityResponseWrapper,
  Entity,
  GenericObject
} from '../types/entities';
import {
  BulkOperationResults,
  ExtendedResults
} from '../types/services';
import {
  EntityTemplate,
  TemplateColumn,
  TemplateOperators,
  TemplateValidation
} from '../types/templates';
import {formatIfValid} from '../utils/DateUtil';
import {Mapped} from '../utils/ObjectUtil';
import {
  createLookupSheet,
  createSubSheet,
  createValidation,
  formatErrors,
  interpolateValidation,
  localizeFormulae,
  statusColumn
} from './TemplateHelpers';

// defines the type definitions for asset template options ...
export type AssetTemplateOptions = {
  assetTypes?: Results;
  categories?: Results;
  groupName?: string;
  overrideAssetTypeColumn?: boolean;
  driverSheet?: {
    columns?: GenericObject[];
    rows?: GenericObject[];
  }
};

const assetTypeColumn = ({validation, options, workbook}): TemplateValidation => {
  const assetTypes    = options?.assetTypes?.toString() ?? '';
  validation.error    = interpolateValidation(validation, 'asset types');
  if (assetTypes !== '') {
    validation.formulae = createLookupSheet({
      sheetName    : 'assetType',
      optionString : assetTypes,
      workbook
    });
  }
  return validation;
};

const assetCategoryColumn = ({validation, options, workbook}): TemplateValidation => {
  const categories    = options?.categories?.toString() ?? '';
  validation.error    = interpolateValidation(validation, 'asset categories');
  if (categories !== '') {
    validation.formulae = createLookupSheet({
      sheetName    : 'assetCategory',
      optionString : categories,
      workbook
    });
  }
  return validation;
};

const driverColumn = ({validation, options, workbook}): TemplateValidation => {
  const {columns, rows} = options.driverSheet || {};
  validation.error      = interpolateValidation(validation, 'Drivers');
  validation.formulae   = createSubSheet({
    sheetName  : 'Drivers',
    targetCell : 'B',
    rows,
    workbook,
    columns
  });
  return validation;
};

const overrideColumn = ({validation}): TemplateValidation => {
  const booleans      = localizeFormulae('common:booleans');
  validation.error    = interpolateValidation(validation, booleans);
  validation.formulae = [`"${booleans}"`];
  return validation;
};

const customAssetTypeColumn = ({validation}): TemplateValidation => {
  const assetTypes    = localizeFormulae('admin-assets:form.options');
  validation.error    = interpolateValidation(validation, assetTypes);
  validation.formulae = [`"${assetTypes}"`];
  return validation;
};

const unitColumn = ({validation}): TemplateValidation => {
  const units         = localizeFormulae('admin-assets:form.units');
  validation.error    = interpolateValidation(validation, units);
  validation.formulae = [`"${units}"`];
  return validation;
};

const fuelTypeColumn = ({validation}): TemplateValidation => {
  const engineTypeOptions = translate('common:editors.engineType.options', {returnObjects: true});
  const labels            = engineTypeOptions?.map(({label}) => label);
  validation.error        = interpolateValidation(validation, labels);
  validation.formulae     = [`"${labels}"`];
  return validation;
};

/**
 * Provides a utility function which manages the dynamic generation of an XLSX
 * spreadsheet specific to Asset imports for download by the User.
 *
 * Asset Template column definitions are defined as localized entries within
 * the admin-assets.json localization documents for each respective locale.
 * Entries take the following form:
 *
 * ```typescript
 *
 * import {assetsTemplate} from '@ion/core/src/xlsx/AssetTemplate';
 *
 * const templates = {
 *   [Entity.asset] : assetsTemplate
 * };
 * ```
 *
 * @param columns An array of localized asset template column definitions.
 * @param options An array of other optional column definitions.
 */
export const assetsTemplate = ({columns, workbook, options = {}}: EntityTemplate) => {
  const columnEntries: TemplateColumn[] = [];
  const columnValidations: TemplateValidation[] = [];
  const templateColumns = {
    [AssetBulkEditorFormFields.Status]    : statusColumn,
    [AssetBulkEditorFormFields.AssetType] : options?.overrideAssetTypeColumn
      ? customAssetTypeColumn : assetTypeColumn,
    [AssetBulkEditorFormFields.AssetCategory]         : assetCategoryColumn,
    [AssetBulkEditorFormFields.OverrideOdometer]      : overrideColumn,
    [AssetBulkEditorFormFields.OverrideEngineRunTime] : overrideColumn,
    [AssetBulkEditorFormFields.OverrideSpeed]         : overrideColumn,
    [AssetBulkEditorFormFields.OdometerReferenceUnit] : unitColumn,
    [AssetBulkEditorFormFields.Driver]                : driverColumn,
    // keyed by the template column's own key rather than
    // AssetBulkEditorFormFields.FuelType, since the column carries the
    // engine type label (also used to pre-fill the template when downloaded
    // with Assets) rather than the fuelType value itself ...
    fuelTypeUI                                        : fuelTypeColumn
  };
  columns.forEach((column, index) => {
    const {key, type, required, max, min, error, width, style} = column;
    const validation = createValidation({required, error, index, type});
    let {header} = column;
    if (error) {
      if (isNumber(max) && required) {
        validation.formulae = [1, max];
        validation.operator = TemplateOperators.Between;
      } else if (isNumber(max)) {
        validation.formulae = [max];
        validation.operator = TemplateOperators.LessThanOrEqual;
      } else if (isNumber(min)) {
        validation.formulae = [min];
        validation.operator = TemplateOperators.GreaterThanOrEqual;
      }
    }
    if (header.includes('__group__')) {
      header = header.replace('__group__', options?.groupName ? `(${options.groupName})` : '');
    }
    columnEntries.push({key, header, width, style});
    columnValidations.push(templateColumns[key]?.({column, validation, workbook, options}) || validation);
  });
  return {
    columnEntries,
    columnValidations
  };
};

/**
 * Provides a utility function which manages the generation of spreadsheet errors
 * specific to Assets import for download by the user.
 *
 * ```typescript
 *
 * import {getAssetImportErrors} from '@ion/core/src/xlsx/AssetTemplate';
 *
 * const processedResult = getAssetImportErrors(invalidAssets, result);
 * ```
 *
 * @param invalidAssets Assets which are invalid to be imported.
 * @param result Response returned from server.
 *
 * @returns The processed results with errors if any
 */
export function getAssetImportErrors(
  invalidAssets: CTCEntityResponseWrapper<Asset>[],
  result: ExtendedResults,
  group: AccountLink
): ExtendedResults {
  if (result?.errors?.length) {
    const formattedErrors = formatErrors(result?.errors, Entity.asset);
    result.errors = [];
    result.errors = [...formattedErrors];
  }
  if (invalidAssets?.length) {
    invalidAssets.forEach(({asset}: any) => {
      let message;
      const {account}   = asset;
      const accountName = group?.title;
      const groupName   = account?.title || (asset.groupName || asset.parentGroup || accountName);
      if (asset.hasDuplicateAccount) {
        message = translate('admin-assets:import.errors.duplicateGroup', {
          parent : accountName
        });
      } else if (asset.groupName || asset.parentGroup) {
        message = translate('admin-assets:import.errors.invalidGroup', {
          asset  : asset.name,
          group  : groupName,
          parent : accountName
        });
      } else if (!asset.account) {
        message = translate('admin-assets:import.errors.noGroup', {
          asset : asset.name
        });
      } else if (asset.isScheduleNotFound) {
        message = translate('admin-assets:import.errors.invalidSchedule', {
          asset    : asset.name,
          schedule : asset.schedule,
          group    : groupName
        });
      } else if (asset.isDeviceNotFound) {
        message = translate('admin-assets:import.errors.deviceNotFound', {
          asset      : asset.name,
          identifier : asset.missingDevices,
          group      : groupName
        });
      } else if (asset.isDeviceAssociationRestricted) {
        message = translate('admin-assets:import.errors.deviceAssociationRestricted');
      } else if (asset.isDriverNotFound) {
        message = translate('admin-assets:import.errors.driverNotFound');
      } else if (asset.isMultipleDrivers) {
        message = translate('admin-assets:import.errors.multipleDrivers');
      } else if (asset.isAssetDriverAlreadyExisting) {
        message = translate('admin-assets:import.errors.driverAlreadyAssignedAsset', {
          asset    : asset.alreadyExistingAsset,
          operator : asset.operators || ''
        });
      } else if (asset.isWrongAccount) {
        message = translate('admin-assets:import.errors.driverWrongGroup');
      } else if (asset.isDeviceMoveFailed) {
        message = translate('admin-assets:import.errors.deviceMoveFailed', {
          asset  : asset.name,
          reason : asset.deviceMoveError
        });
      } else {
        message = translate('admin-assets:import.errors.invalidGroupESN', {
          group : groupName,
          asset : asset.name
        });
      }
      result?.errors?.push({
        error : message,
        name  : asset.name,
        message,
        asset
      });
    });
  }
  return result;
};

/**
 * Provides a utility function which maps the results against the template columns
 * before the same is available as excel for user to download.
 *
 * ```typescript
 *
 * import {exportMapper} from '@ion/core/src/xlsx/AssetTemplate';
 *
 * const processedResult = exportMapper(invalidAssets, result);
 * ```
 *
 * @param results Response from server.
 * @param boolean Checks if response has errors only.
 * @param Mapped Map which helps in mapping the results against respective
 * template columns.
 *
 * @returns The results mapped against the respective columns for generating
 * excel data.
 */
export function exportMapper(
  {results, errors}: BulkOperationResults | ExtendedResults,
  errorsOnly: boolean,
  attributes: Mapped
): unknown[] {
  const entries      = [];
  const items        = errorsOnly ? errors : results.concat(errors);
  const succeeded    = translate('common:succeeded');
  const failed       = translate('common:failed');
  const unavailable  = translate('common:notAvailable');
  items?.forEach(item => {
    const {
      asset,
      error,
      code,
      message
    } = item;
    const entry = [];
    const isGlobalError = !isUndefined(code);
    const hasErrors = !isUndefined(error) || isGlobalError;
    const result    = hasErrors ? asset : item;
    Object.values(attributes)?.forEach((attribute: string) => {
      if (!isGlobalError || asset) {
        const val = result[attribute];
        switch (attribute) {
          case 'groupName':
            entry.push(result.account ? result.account.title : result.groupName);
            break;
          case 'devices':
            entry.push(val?.length ? val[0].title : unavailable);
            break;
          case 'ioProfile':
            entry.push(val ? val.title : unavailable);
            break;
          case 'schedule':
            entry.push(result?.schedules?.length ? result.schedules[0].title : unavailable);
            break;
          case 'installDate' :
            entry.push(formatIfValid(val));
            break;
          case 'uninstallDate' :
            entry.push(formatIfValid(val));
            break;
          case 'odometerReferenceTime' :
            entry.push(formatIfValid(val));
            break;
          case 'engineHoursReferenceTime' :
            entry.push(formatIfValid(val));
            break;
          case 'operators':
            entry.push(val?.length ? val[0].title : null);
            break;
          default :
            entry.push(val);
            break;
        }
      } else {
        entry.push(unavailable);
      }
    });
    entry.push(hasErrors ? failed : succeeded);
    entry.push(hasErrors ? (!isGlobalError ? error?.message || error : message) : '');
    entries.push(entry);
  });
  return entries;
};
